// ─────────────────────────────────────────────
//  GrowthTable.jsx
//  Tabla de proyección — soporta días y meses
// ─────────────────────────────────────────────

import React from 'react';
import styles from './GrowthTable.module.css';
import { formatCurrency } from '../../utils/format';
import { formatTerm } from './TermPicker';

// Calcula el balance en un termPos dado
function calcBalance(capital, effectiveRate, termPos, isCompound) {
	const r = effectiveRate / 100;
	if (termPos <= 29) {
		const days = termPos;
		return isCompound
			? capital * Math.pow(1 + r / 365, days)
			: capital * (1 + r * (days / 365));
	}
	const months = termPos - 29;
	return isCompound
		? capital * Math.pow(1 + r / 12, months)
		: capital * (1 + r * (months / 12));
}

// Genera checkpoints significativos (en termPos units)
function getCheckpoints(termPos) {
	if (termPos <= 7) {
		return Array.from({ length: termPos }, (_, i) => i + 1);
	}
	if (termPos <= 29) {
		// Días: semanales + final
		const pts = [];
		for (let d = 7; d <= termPos; d += 7) pts.push(d);
		if (pts[pts.length - 1] !== termPos) pts.push(termPos);
		return pts;
	}

	// Meses
	const months = termPos - 29;

	if (months <= 12) {
		return Array.from({ length: months }, (_, i) => i + 1 + 29);
	}
	if (months <= 24) {
		const pts = [];
		for (let m = 3; m <= months; m += 3) pts.push(m + 29);
		if ((pts[pts.length - 1] - 29) !== months) pts.push(months + 29);
		return pts;
	}
	if (months <= 60) {
		const pts = [];
		for (let m = 6; m <= months; m += 6) pts.push(m + 29);
		if ((pts[pts.length - 1] - 29) !== months) pts.push(months + 29);
		return pts;
	}
	// Años
	const pts = [];
	for (let m = 12; m <= months; m += 12) pts.push(m + 29);
	if ((pts[pts.length - 1] - 29) !== months) pts.push(months + 29);
	return pts;
}

export default function GrowthTable({ capital, effectiveRate, termPos, parsedIsr, isCompound }) {
	const checkpoints   = getCheckpoints(termPos);
	const finalBalance  = calcBalance(capital, effectiveRate, termPos, isCompound);
	const hasIsr        = parsedIsr > 0;

	return (
		<section className={styles.card} aria-label="Proyección de crecimiento">
			<p className={styles.title}>Proyección de crecimiento</p>

			<div className={styles.tableWrap} role="table" aria-label="Tabla de crecimiento">

				{/* Header */}
				<div className={`${styles.tableRow} ${styles.tableHead}`} role="row">
					<span role="columnheader">Período</span>
					<span role="columnheader">Balance</span>
					<span role="columnheader">Ganancia{hasIsr ? ' neta' : ''}</span>
					<span role="columnheader">%</span>
				</div>

				{/* Rows */}
				{checkpoints.map((pos) => {
					const balance   = calcBalance(capital, effectiveRate, pos, isCompound);
					const grossGain = balance - capital;
					const netGain   = grossGain * (1 - parsedIsr / 100);
					const pct       = (netGain / capital) * 100;
					const barPct    = Math.min(100, (balance / finalBalance) * 100);
					const isFinal   = pos === termPos;

					return (
						<div
							key={pos}
							className={`${styles.tableRow} ${isFinal ? styles.finalRow : ''}`}
							role="row"
						>
							<span className={styles.period} role="cell">
								{formatTerm(pos)}
							</span>
							<span className={styles.balance} role="cell">
								{formatCurrency(capital + netGain)}
							</span>
							<span className={styles.gain} role="cell">
								+{formatCurrency(netGain).replace('$ ', '')}
							</span>
							<span className={styles.pctCell} role="cell">
								<span className={styles.pctBar}>
									<span
										className={styles.pctFill}
										style={{ width: `${barPct}%` }}
										aria-hidden="true"
									/>
								</span>
								<span className={styles.pctNum}>{pct.toFixed(1)}%</span>
							</span>
						</div>
					);
				})}
			</div>
		</section>
	);
}
